import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Text } from '@/components/Text';
import { useStoreActions, useStoreState } from '@/stores/store';
import { CustomCheckBox } from '@/components/SharedComponents/CustomCheckBox/';
import { fetchUserConsents } from '@/api/services/user';
import colors, { appTheme } from '@/config/colors';
import {
  connectWithEpa,
  deleteEpaLinked,
  getEpaUser,
} from '@/api/services/epaAuth';
import { updateConsent } from '@/utils/consents';
import { EpaAuthContext } from '@/contexts/EpaAuthContext';
import { logError } from '@/utils/logHandling';

import { styles } from './styles';
import { Divider } from 'react-native-elements';
import { Button } from '@/components/ui/therapist/Button';
import { Modal } from '@/components/ui/therapist/Modal';
import { Icon } from '@/components/Icon';
import theme from '@/config/theme';
import { DataTransferCard } from './DataTransferCard';
import { ConnectionStatusCard } from './ConnectionStatusCard';

export const EpaScreen = () => {
  const { storedUser, userConsents } = useStoreState((state) => ({
    storedUser: state.signedInUser,
    userConsents: state.userConsents,
  }));

  const { setUserInsuranceId, setUserEpa } = useStoreActions((actions) => ({
    setUserInsuranceId: actions.setUserInsuranceId,
    setUserEpa: actions.setUserEpa,
  }));

  const [hasDataProcessingConsent, setHasDataProcessingConsent] =
    useState<boolean>(false);
  let [showEpaConnectError, setShowEpaConnectError] = useState<boolean>(false);
  let [showEpaDisconnectError, setShowEpaDisconnectError] =
    useState<boolean>(false);

  const authContext = useContext(EpaAuthContext);

  useEffect(() => {
    fetchUserConsents()
      .then(({ data }) =>
        setHasDataProcessingConsent(
          data.find((c) => c.name === 'WriteToEpa')?.givenConsent ?? false,
        ),
      )
      .catch((error) => logError('Failed to fetch user consents', error));
  }, []);

  useEffect(() => {
    if (!storedUser.user?.idpInsuranceId) {
      return;
    }
    (async () => {
      try {
        const userEpa = await getEpaUser();
        setUserEpa(userEpa);
      } catch (error) {
        logError('Failed to load ePA user', error);
      }
    })();
  }, [storedUser.user?.idpInsuranceId, setUserEpa]);

  const { canWriteToEpa, writeToEpaConsentId } = useMemo(() => {
    const consent = userConsents.find((c) => c.name === 'WriteToEpa');
    return {
      canWriteToEpa: consent?.givenConsent ?? false,
      writeToEpaConsentId: consent?._id,
    };
  }, [userConsents]);

  const handleDisconnectEpa = async () => {
    try {
      await deleteEpaLinked();
      setUserInsuranceId(undefined);
      setUserEpa(undefined);
    } catch (error) {
      logError('Failed to disconnect ePA', error);
      setShowEpaDisconnectError(true);
    }
  };

  const handleConnectWithEpa = async () => {
    if (writeToEpaConsentId) {
      try {
        await updateConsent(writeToEpaConsentId, hasDataProcessingConsent);
      } catch (_error) {
        logError('Failed to update WriteToEpa consent');
      }
    }
    await connectWithEpa(
      authContext,
      (insuranceId) => {
        setUserInsuranceId(insuranceId);
        fetchUserConsents();
      },
      () => {
        setShowEpaConnectError(true);
      },
    );
  };

  return (
    <SafeAreaView style={styles.fullHeight}>
      <ScrollView style={{ width: '100%' }}>
        <View style={styles.container}>
          <View style={[styles.cardContainer, { width: '70%' }]}>
            <View style={[styles.cardSection, styles.headingRow]}>
              <Icon
                name="fileText"
                size={20}
                color={appTheme.primary}
                style={styles.headingIcon}
              />
              <Text style={styles.heading}>Was ist die ePA?</Text>
            </View>
            <Divider color={colors.lightGrey} width={2} />
            <View style={styles.cardSection}>
              <Text style={styles.text}>
                Alle gesetzlich Versicherten können eine elektronische
                Patientenakte (ePA) von ihrer Krankenkasse erhalten, in der
                medizinische Befunde und weitere persönliche Gesundheitsdaten
                gespeichert werden. Sie können selbst bestimmen, ob und in
                welchem Umfang Sie die ePA nutzen möchten.
              </Text>
            </View>
            <Divider color={colors.lightGrey} width={2} />
            <View style={styles.cardSection}>
              <Text style={styles.text}>
                Sie haben hierbei auch die Möglichkeit, die Übungsdauer und
                Übungsergebnisse aus neolexon in Ihre ePA zu übertragen. Bei der
                automatischen Übertragung werden vergangene Übungsdaten jeden
                Tag um 22:00 Uhr in die ePA übertragen.
              </Text>
            </View>
          </View>

          <View style={{ width: '70%' }}>
            {storedUser.user?.idpInsuranceId ? (
              <>
                <ConnectionStatusCard onDisconnect={handleDisconnectEpa} />
                <DataTransferCard canWriteToEpa={canWriteToEpa} />
                <Modal
                  isVisible={showEpaDisconnectError}
                  onClose={() => setShowEpaDisconnectError(false)}
                  title="Verbindung trennen fehlgeschlagen"
                  cancelActionButton={
                    <Button
                      label="Abbrechen"
                      look="ghost"
                      onPress={() => setShowEpaDisconnectError(false)}
                    />
                  }
                  confirmActionButton={
                    <Button
                      label="Erneut versuchen"
                      look="primary"
                      onPress={() => {
                        setShowEpaDisconnectError(false);
                        handleDisconnectEpa();
                      }}
                    />
                  }
                >
                  <Text style={[styles.text, styles.textCenter]}>
                    Die Verbindung zur ePA konnte nicht getrennt werden. Bitte
                    versuchen Sie es erneut oder kontaktieren Sie unseren
                    Support, falls das Problem weiterhin besteht.
                  </Text>
                </Modal>
              </>
            ) : (
              <>
                <View style={styles.cardContainer}>
                  <View style={[styles.cardSection, styles.headingRow]}>
                    <Icon
                      name="shield"
                      size={20}
                      color={appTheme.primary}
                      style={styles.headingIcon}
                    />
                    <Text style={styles.heading}>Einwilligung</Text>
                  </View>
                  <Divider color={colors.lightGrey} width={2} />
                  <View
                    style={[
                      styles.cardSection,
                      { flexDirection: 'row', alignItems: 'flex-start' },
                    ]}
                  >
                    <CustomCheckBox
                      checked={hasDataProcessingConsent}
                      onChecked={(value: boolean) => {
                        setHasDataProcessingConsent(!value);
                      }}
                    />
                    <Pressable
                      style={{ flex: 1 }}
                      onPress={() =>
                        setHasDataProcessingConsent(!hasDataProcessingConsent)
                      }
                    >
                      <Text style={styles.text}>
                        Ich willige ein, dass Übungsdauer und Übungsergebnisse
                        von neolexon in meiner persönlichen elektronischen
                        Patientenakte gespeichert werden. Ich kann meine
                        Einwilligung jederzeit widerrufen.
                      </Text>
                    </Pressable>
                  </View>
                </View>

                <View
                  style={[styles.hintRow, { marginTop: theme.spacings.lg }]}
                >
                  <Icon
                    name="exclamationCircle"
                    size={20}
                    color={colors.darkGrey}
                    style={styles.headingIcon}
                  />
                  <Text style={[styles.textSmall, { flex: 1 }]}>
                    Hinweis: hierzu müssen Sie sich einmalig mit Ihrer
                    GesundheitsID anmelden, um sich mit der ePA zu verbinden.
                  </Text>
                </View>

                <Button
                  style={{
                    marginTop: 20,
                    justifyContent: 'center',
                    width: '100%',
                  }}
                  onPress={handleConnectWithEpa}
                  disabled={!hasDataProcessingConsent}
                  label="Verbinden"
                />
                <Modal
                  isVisible={showEpaConnectError}
                  onClose={() => setShowEpaConnectError(false)}
                  title="Verbindung fehlgeschlagen"
                  cancelActionButton={
                    <Button
                      label="Abbrechen"
                      look="ghost"
                      onPress={() => setShowEpaConnectError(false)}
                    />
                  }
                  confirmActionButton={
                    <Button
                      label="Erneut versuchen"
                      look="primary"
                      onPress={() => {
                        setShowEpaConnectError(false);
                        handleConnectWithEpa();
                      }}
                    />
                  }
                >
                  <Text style={[styles.text, styles.textCenter]}>
                    Die Verbindung mit der GesundheitsID konnte nicht
                    hergestellt werden. Bitte versuchen Sie es erneut oder
                    kontaktieren Sie unseren Support, falls das Problem
                    weiterhin besteht.
                  </Text>
                </Modal>
              </>
            )}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};
